import { useEffect, useRef, useState } from "react";
import {
  createOptionsChart,
  createSeriesMarkers,
  CrosshairMode,
  IChartApiBase,
  ISeriesApi,
  LineData,
  LineSeries,
  SeriesMarker,
} from "lightweight-charts";
import { IChartData, ILineChartDataset } from "../../Interfaces/commonDtos";
import { getPriceChartOptions } from "./lwcDefaults";
import { resolveSeriesColor } from "./chartColors";
import { observeChartResize } from "./observeChartResize";
import { createChartRefreshState } from "./chartRefreshState";

interface IProps {
  data: IChartData<ILineChartDataset>;
  className?: string;
  yAxisTitle?: string;
  xAxisTitle?: string;
  maintainAspectRatio?: boolean;
}

function toNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function fractionDigits(value: number): number {
  if (!Number.isFinite(value) || Math.abs(value - Math.round(value)) < 1e-9) {
    return 0;
  }
  const text = String(value);
  if (text.includes("e") || text.includes("E")) return 6;
  const dot = text.indexOf(".");
  return dot === -1 ? 0 : Math.min(6, text.length - dot - 1);
}

function maxFractionDigits(values: number[]): number {
  return values.reduce((max, value) => Math.max(max, fractionDigits(value)), 0);
}

function formatNumericTick(value: number, precision: number): string {
  if (!Number.isFinite(value)) return "";
  const digits = Math.max(0, precision);
  if (digits <= 0) {
    const rounded = Math.round(value);
    return rounded === 0 ? "0" : rounded.toLocaleString("en-US");
  }
  const rounded = Number(value.toFixed(digits));
  if (rounded === 0) return "0";
  return rounded.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

function formatTooltipValue(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "number" && Number.isFinite(value)) {
    return value.toString();
  }
  return String(value);
}

export function LineChart({
  data,
  className,
  xAxisTitle,
  yAxisTitle,
  maintainAspectRatio = true,
}: IProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApiBase<number> | null>(null);
  const seriesRefs = useRef<ISeriesApi<"Line", number>[]>([]);
  const rawBySeriesRef = useRef<Map<number, { x: unknown; y: unknown }>[]>([]);
  const xPrecisionRef = useRef(2);
  const yPrecisionRef = useRef(4);
  const refreshStateRef = useRef(createChartRefreshState());
  const tooltipMoveRef = useRef<(param: any) => void>(() => {});
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    lines: string[];
    placeLeft: boolean;
    placeAbove: boolean;
  } | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const refreshState = refreshStateRef.current;

    const formatX = (time: number) =>
      formatNumericTick(time, xPrecisionRef.current);
    const formatY = (price: number) =>
      formatNumericTick(price, yPrecisionRef.current);

    const chart = createOptionsChart(
      el,
      getPriceChartOptions({
        localization: {
          precision: 4,
          timeFormatter: formatX,
          priceFormatter: formatY,
        } as any,
        timeScale: {
          borderVisible: true,
          ticksVisible: true,
          minimumHeight: 28,
          tickMarkMaxCharacterLength: 12,
        },
        crosshair: {
          mode: CrosshairMode.Magnet,
          vertLine: {
            visible: true,
            labelVisible: true,
            color: "#758696",
            labelBackgroundColor: "#4c525e",
          },
          horzLine: {
            visible: true,
            labelVisible: true,
            color: "#758696",
            labelBackgroundColor: "#4c525e",
          },
        },
      })
    );
    chartRef.current = chart;

    const onMove = (param: any) => {
      if (
        !param?.point ||
        param.point.x < 0 ||
        param.point.y < 0 ||
        !param.seriesData?.size
      ) {
        setTooltip(null);
        return;
      }

      const lines: string[] = [];
      let xLabel = "";
      seriesRefs.current.forEach((series, idx) => {
        const raw = param.seriesData?.get(series) as
          | { time?: number; value?: number }
          | undefined;
        if (!raw || raw.value == null) return;
        const original =
          raw.time != null
            ? rawBySeriesRef.current[idx]?.get(raw.time)
            : undefined;
        if (!xLabel) {
          xLabel = formatTooltipValue(original?.x ?? raw.time);
        }
        lines.push(`Value: ${formatTooltipValue(original?.y ?? raw.value)}`);
      });
      if (xLabel) lines.unshift(xLabel);

      if (!lines.length) {
        setTooltip(null);
        return;
      }

      const plotWidth = el.clientWidth || 300;
      const plotHeight = el.clientHeight || 180;

      setTooltip({
        x: param.point.x,
        y: param.point.y,
        lines,
        placeLeft: param.point.x > plotWidth / 2,
        placeAbove: param.point.y > plotHeight / 2,
      });
    };

    tooltipMoveRef.current = onMove;
    const handleMove = (param: any) =>
      refreshState.onCrosshairMove(param, onMove);
    chart.subscribeCrosshairMove(handleMove);

    const stopObserving = observeChartResize(el, (width, height) => {
      if (width <= 0 || height <= 0) return;
      chart.applyOptions({ width, height });
    });

    return () => {
      stopObserving();
      chart.unsubscribeCrosshairMove(handleMove);
      refreshState.reset();
      chart.remove();
      chartRef.current = null;
      seriesRefs.current = [];
      rawBySeriesRef.current = [];
    };
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    refreshStateRef.current.beginUpdate(chart);

    seriesRefs.current.forEach((s) => {
      try {
        chart.removeSeries(s);
      } catch {
        /* already removed */
      }
    });
    seriesRefs.current = [];
    rawBySeriesRef.current = [];

    const labels = data?.labels ?? [];
    const xValues = labels
      .map((label) => toNumber(label))
      .filter((value): value is number => value != null);
    const precision = maxFractionDigits(xValues);
    xPrecisionRef.current = precision;

    const yValues = (data?.datasets ?? []).flatMap((dataset) =>
      (dataset.data ?? [])
        .map((value) => toNumber(value))
        .filter((value): value is number => value != null)
    );
    const yPrecision = Math.min(6, Math.max(2, maxFractionDigits(yValues)));
    yPrecisionRef.current = yPrecision;

    chart.applyOptions({
      localization: {
        precision,
        timeFormatter: (time: number) => formatNumericTick(time, precision),
        priceFormatter: (price: number) => formatNumericTick(price, yPrecision),
      } as any,
    });

    (data?.datasets ?? []).forEach((dataset, datasetIndex) => {
      const color = resolveSeriesColor(dataset.borderColor, datasetIndex);
      const series = chart.addSeries(LineSeries, {
        color,
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: false,
        baseLineVisible: false,
        crosshairMarkerVisible: true,
        title: "",
        priceScaleId: "left",
        priceFormat: {
          type: "price",
          precision: yPrecision,
          minMove: Number((10 ** -yPrecision).toFixed(yPrecision)),
        },
      });

      const points: LineData<number>[] = [];
      const markers: SeriesMarker<number>[] = [];
      const rawByTime = new Map<number, { x: unknown; y: unknown }>();

      labels.forEach((label, i) => {
        const x = toNumber(label);
        const original = dataset.data?.[i];
        const y = toNumber(original);
        if (x == null || y == null) return;

        points.push({ time: x, value: y });
        rawByTime.set(x, { x: label, y: original });

        const pointColor = Array.isArray(dataset.borderColor)
          ? dataset.borderColor[i]
          : undefined;
        const radius = Array.isArray(dataset.pointRadius)
          ? dataset.pointRadius[i]
          : undefined;

        if (pointColor === "#dc2626" || (radius != null && radius >= 3)) {
          markers.push({
            time: x,
            position: "inBar",
            color: "#dc2626",
            shape: "circle",
            size: 1.5,
          });
        }
      });

      points.sort((a, b) => (a.time as number) - (b.time as number));
      const deduped: LineData<number>[] = [];
      for (const p of points) {
        const last = deduped[deduped.length - 1];
        if (last && last.time === p.time) {
          deduped[deduped.length - 1] = p;
        } else {
          deduped.push(p);
        }
      }

      series.setData(deduped);
      if (markers.length) {
        createSeriesMarkers(series, markers);
      }
      seriesRefs.current.push(series);
      rawBySeriesRef.current.push(rawByTime);
    });

    const times = Array.from(new Set(
      seriesRefs.current.flatMap((series) => series.data().map((point) => point.time))
    )).sort((a, b) => a - b);
    refreshStateRef.current.finishUpdate(
      chart, times, seriesRefs.current, tooltipMoveRef.current,
    );
  }, [data]);

  const fill = !maintainAspectRatio;

  return (
    <div
      className={`relative w-full ltr h-full min-h-0 min-w-0 overflow-hidden ${
        fill ? "p-0.5" : "p-3"
      } ${maintainAspectRatio ? "min-h-[220px]" : ""}`}
      dir="ltr"
      style={{ direction: "ltr" }}
    >
      {yAxisTitle ? (
        <p className="absolute -top-5 left-2 text-hint text-grayText text-xs">
          {yAxisTitle}
        </p>
      ) : null}
      {xAxisTitle ? (
        <p className="absolute bottom-15 right-0 text-hint text-grayText text-xs">
          {xAxisTitle}
        </p>
      ) : null}
      <div
        className={`relative w-full h-full min-w-0 min-h-0 ${
          fill ? "" : "min-h-[180px]"
        }`}
      >
        <div
          ref={containerRef}
          className={`absolute inset-0 ${className || ""}`}
        />
        {tooltip ? (
          <div
            className="pointer-events-none absolute z-20 max-w-xs rounded border border-gray-200 bg-white px-2 py-1 text-xs shadow-md text-left text-gray-800"
            dir="ltr"
            style={{
              left: tooltip.x,
              top: tooltip.y,
              transform: `translate(${
                tooltip.placeLeft ? "calc(-100% - 8px)" : "12px"
              }, ${tooltip.placeAbove ? "calc(-100% - 8px)" : "12px"})`,
            }}
          >
            {tooltip.lines.map((line, i) => (
              <div key={`${i}-${line}`} className="leading-5 whitespace-nowrap">
                {line}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
